/**
 * The NexERP mark and wordmark, as the platform console wears them, and the
 * tile an app is known by.
 *
 * The same N-with-a-node as the NexERP app, so the two read as one product —
 * with an "Admin" tag beside the wordmark, because the first thing anybody on
 * this console must never be unsure of is that they are in the staff console,
 * where a change reaches every organization.
 */
import { useId } from 'react';
import { Icon } from '../components/Icon.jsx';

export function BrandMark({ size = 28, className = '' }) {
  const id = useId();
  const gradient = `nex-mark-${id.replace(/[^a-zA-Z0-9-]/g, '')}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      className={`brand-mark-svg ${className}`}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradient} x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#7b6bff" />
          <stop offset="0.45" stopColor="#5b4cf0" />
          <stop offset="1" stopColor="#2f86ff" />
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="8" fill={`url(#${gradient})`} />
      <rect x="0.5" y="0.5" width="31" height="31" rx="7.5" fill="none" stroke="rgba(255,255,255,0.18)" />
      <path
        d="M10.5 22.5V10.5l10.5 12V12.8"
        fill="none"
        stroke="#fff"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="21" cy="9.6" r="2.3" fill="#fff" />
    </svg>
  );
}

/** Mark + "NexERP" + the Admin tag. `compact` shows the mark alone. */
export function Brand({ compact = false, size = 26 }) {
  return (
    <span className="brand">
      <BrandMark size={size} />
      {!compact && (
        <>
          <span className="brand-word">
            Nex<span className="brand-word-strong">ERP</span>
          </span>
          <span className="brand-tag">Admin</span>
        </>
      )}
    </span>
  );
}

/**
 * An app's icon tile: its identity hue as a soft gradient, its glyph in white.
 * `app` needs `key` and `icon`; sizes are sm (22), md (30), lg (40), xl (52).
 */
export function AppIcon({ app, size = 'md' }) {
  const glyph = { sm: 13, md: 16, lg: 21, xl: 26 }[size] ?? 16;
  return (
    <span className={`app-icon app-icon-${size}`} data-app={app?.key ?? 'home'} aria-hidden="true">
      <Icon name={app?.icon ?? 'home'} size={glyph} strokeWidth={size === 'sm' ? 2 : 1.9} />
    </span>
  );
}
